import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Backdrop,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";
import StyledTextField from "./StyledTextField";
import api from "../../api/config";
import { useNotification } from "../../context/NotificationContext";
import { AddUser, UpdateUser } from "../../api/Users";
import { AddAccessLevelToUser } from "../../api/Access";

const Header = styled(DialogTitle)`
  background-color: #28929c;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`;

const Content = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px !important;
  background-color: #2b3342;
  color: white;
`;

export default function NewUserModal({
  open,
  onClose,
  accessLevels = [],
  onSaveSuccess = () => {},
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [accessLevel, setAccessLevel] = useState({ name: "", uuid: "" });
  const [clientUuid, setClientUuid] = useState("");
  const [clientName, setClientName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { showNotification } = useNotification();

  useEffect(() => {
    const clientOrganization = localStorage.getItem("organization");
    const clientOrganizationUuid = localStorage.getItem("organization_uuid");
    setClientUuid(clientOrganizationUuid);
    setClientName(clientOrganization);
  }, []);

  const handleSave = async () => {
    const newErrors = {};

    if (!firstName.trim()) newErrors.firstName = "First Name is required";
    if (!lastName.trim()) newErrors.lastName = "Last Name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    if (!accessLevel) newErrors.accessLevel = "Access Level is required";
    if (!phoneNumber.trim()) newErrors.phoneNumber = "Phone Number is required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const payload = {
      email,
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
      organization: clientName,
      organization_uuid: clientUuid,
      login_location: "dashboard",
    };

    const payload2 = {
      email,
      accessLevelUID: accessLevel.uuid,
    };

    const payload3 = {
      email,
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
      organization: clientName,
      organization_uuid: clientUuid,
    };

    console.log("Payload:", payload2);

    setLoading(true);
    try {
      const response = await api.post(`${AddUser}`, payload);

      const accessLevelPromise = api
        .post(`${AddAccessLevelToUser}`, payload2)
        .catch((err) => {
          console.error("Failed to assign access level:", err);
          showNotification("Failed to assign access level", "error");
        });

      const updateUserPromise = api
        .put(`${UpdateUser}`, payload3)
        .catch((err) => {
          console.error("Failed to update user organization:", err);
          showNotification("Failed to update user organization", "error");
        });

      await Promise.all([accessLevelPromise, updateUserPromise]);

      showNotification("User created successfully", "success");
      onSaveSuccess();
      handleClose();
    } catch (error) {
      console.error("Error adding user:", error);
      showNotification("Error adding user", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhoneNumber("");
    setAccessLevel({ name: "", uuid: "" });
    setClientUuid("");
    setErrors({});
    onClose();
  };

  const transformedAccessLevels = accessLevels.map((item) => ({
    label: item.name,
    uuid: item.uuid,
  }));

  useEffect(() => {
    console.log("Access Levels:", accessLevels);
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Header>
        Add User
        <IconButton onClick={handleClose} style={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </Header>

      <Content>
        <StyledTextField
          label="First Name"
          variant="outlined"
          fullWidth
          required
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            setErrors((prev) => ({
              ...prev,
              firstName: e.target.value ? "" : "First Name is required",
            }));
          }}
          error={Boolean(errors.firstName)}
          helperText={errors.firstName}
        />

        <StyledTextField
          label="Last Name"
          variant="outlined"
          fullWidth
          required
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            setErrors((prev) => ({
              ...prev,
              lastName: e.target.value ? "" : "Last Name is required",
            }));
          }}
          error={Boolean(errors.lastName)}
          helperText={errors.lastName}
        />

        <Autocomplete
          options={transformedAccessLevels}
          getOptionLabel={(option) => option.label || ""}
          value={
            transformedAccessLevels.find(
              (al) => al.uuid === accessLevel.uuid
            ) || null
          }
          onChange={(event, newValue) => {
            setAccessLevel({
              name: newValue ? newValue.label : "",
              uuid: newValue ? newValue.uuid : "",
            });
            setErrors((prev) => ({
              ...prev,
              accessLevel: newValue ? "" : "Access Level is required",
            }));
          }}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label="Access Level"
              variant="outlined"
              required
              error={Boolean(errors.accessLevel)}
              helperText={errors.accessLevel}
            />
          )}
          componentsProps={{
            paper: {
              sx: {
                backgroundColor: "#141b25",
                color: "#FBFEFF",
              },
            },
          }}
        />
        <StyledTextField
          label="Email"
          variant="outlined"
          fullWidth
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setErrors((prev) => ({
              ...prev,
              email: e.target.value ? "" : "Email is required",
            }));
          }}
          error={Boolean(errors.email)}
          helperText={errors.email}
        />

        <StyledTextField
          label="Phone Number"
          variant="outlined"
          fullWidth
          required
          value={phoneNumber}
          onChange={(e) => {
            setPhoneNumber(e.target.value);
            setErrors((prev) => ({
              ...prev,
              phoneNumber: e.target.value ? "" : "Phone Number is required",
            }));
          }}
          error={Boolean(errors.phoneNumber)}
          helperText={errors.phoneNumber}
        />
      </Content>

      <DialogActions sx={{ backgroundColor: "#28929c" }}>
        <Button onClick={handleSave} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>

      <Backdrop open={loading} style={{ zIndex: 1300, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}
